import axios from 'axios';
import { AUTH_TOKEN, REFRESH_TOKEN } from 'redux/constants/servicio-autenticacion/Auth';

const { REACT_APP_API_URL } = process.env;

// Promesa compartida para evitar multiples llamadas simultaneas al endpoint
// de refresh cuando varias peticiones fallan con 401 al mismo tiempo.
let refreshPromise = null;

/**
 * Limpia las credenciales almacenadas y redirige al login.
 * Se utiliza cuando el refresh token ya no es valido.
 */
const forceSignOut = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem('USER_NAME');
  localStorage.removeItem('USER_ID');
  window.location.href = '/';
};

/**
 * Solicita un nuevo par de tokens al servicio de autenticacion usando el
 * refresh token almacenado. Devuelve el nuevo access token o null si no
 * fue posible renovar la sesion.
 */
export const refreshAccessToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!storedRefreshToken) {
    return Promise.resolve(null);
  }

  refreshPromise = axios
    .post(`${REACT_APP_API_URL}/api/token/refresh`, {
      refreshToken: storedRefreshToken,
    })
    .then((response) => {
      const { token, refreshToken: newRefreshToken } = response.data || {};
      if (!token) {
        return null;
      }
      localStorage.setItem(AUTH_TOKEN, token);
      if (newRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
      }
      // Disparar evento para que la app actualice el estado de redux
      window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: { token } }));
      return token;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Adjunta un interceptor de respuesta a una instancia de axios que, ante un
 * 401 (token expirado), intenta renovar la sesion con el refresh token y
 * reintenta la peticion original una sola vez. Si la renovacion falla,
 * cierra la sesion.
 */
export const attachRefreshInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};
      const status = error.response ? error.response.status : null;

      const isRefreshCall = String(originalRequest.url || '').includes('/api/token/refresh');

      if (status === 401 && !originalRequest._retryWithRefresh && !isRefreshCall) {
        originalRequest._retryWithRefresh = true;

        const newToken = await refreshAccessToken();
        if (newToken) {
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: newToken,
          };
          return axiosInstance(originalRequest);
        }

        forceSignOut();
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export default {
  refreshAccessToken,
  attachRefreshInterceptor,
};
